import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  Empty,
  List,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  DATE_TIME_FORMAT,
  DEFAULT_DATE_FORMAT,
  RECORDING_STATUS,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import LoaderComponent from '../../components/LoaderComponent';
import { RECORDING_DETAIL } from './graphql/Queries';

const PROCESSING_POLL_INTERVAL = 10000;

const actionItemColumns = [
  { title: 'Task', dataIndex: 'task', key: 'task' },
  { title: 'Owner', dataIndex: 'owner', key: 'owner' },
  {
    title: 'Deadline',
    dataIndex: 'deadline',
    key: 'deadline',
    render: (value) =>
      value ? dayjs(value)?.format(DEFAULT_DATE_FORMAT) : '-',
  },
  { title: 'Priority', dataIndex: 'priority', key: 'priority' },
  {
    title: 'Completed',
    dataIndex: 'completed',
    key: 'completed',
    align: 'center',
    render: (value) => (value ? 'Yes' : 'No'),
  },
];

function ViewRecording() {
  const { recordingId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [shouldPoll, setShouldPoll] = useState(true);

  const queryParams = new URLSearchParams(location?.search);
  const workspaceId = queryParams?.get('workspaceId');
  // display-only — travels via router state from the Recordings list (not the URL), so a
  // shared/refreshed link just shows '-' here instead of a broken/missing value
  const workspaceName = location?.state?.workspaceName;

  const { data, loading } = useQuery(RECORDING_DETAIL, {
    variables: { id: recordingId, workspaceId },
    skip: !workspaceId,
    fetchPolicy: 'network-only',
    pollInterval: shouldPoll ? PROCESSING_POLL_INTERVAL : 0,
    onError() {},
    onCompleted: (res) => {
      const status = res?.recording?.recording?.status;
      setShouldPoll(['AWAITING_UPLOAD', 'PROCESSING']?.includes(status));
    },
  });

  const detail = data?.recording;
  const recording = detail?.recording;
  const isProcessing = ['AWAITING_UPLOAD', 'PROCESSING']?.includes(
    recording?.status,
  );
  const isFailed = recording?.status === 'FAILED';

  if (loading && !detail) {
    return (
      <div className="recording-processing-state">
        <LoaderComponent size="large" setHeight={140} />
      </div>
    );
  }

  if (!workspaceId) {
    return (
      <div className="recording-processing-state">
        <Empty description="This link is missing its workspace reference — open this recording from the Recordings list instead." />
      </div>
    );
  }

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title={
          <>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => history?.goBack()}
            />
            {recording?.title}
          </>
        }
      >
        <div className="card-body-wrapper">
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Project">
              {recording?.project?.name || detail?.contextName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Workspace">
              {workspaceName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {recording?.createdAt
                ? dayjs(recording?.createdAt)?.format(DATE_TIME_FORMAT)
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Status" span={2}>
              <Tag color={RECORDING_STATUS?.[recording?.status]?.color}>
                {RECORDING_STATUS?.[recording?.status]?.label ||
                  recording?.status}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
          <div className="p-24">
            {isFailed && (
              <Alert
                className="recording-audio-player"
                type="error"
                showIcon
                message="Recording failed"
                description="MeetSense could not process this recording. Record again from the mobile app to retry."
              />
            )}

            {isProcessing && (
              <div className="recording-processing-state">
                <Empty
                  description={
                    recording?.status === 'AWAITING_UPLOAD'
                      ? 'Waiting for the recording to reach MeetSense…'
                      : 'MeetSense is processing this recording — this page updates automatically.'
                  }
                />
              </div>
            )}

            {!isProcessing && !isFailed && (
              <>
                {detail?.audioUrl && (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <audio
                    className="recording-audio-player"
                    controls
                    src={detail?.audioUrl}
                  />
                )}

                {detail?.debrief?.summary && (
                  <>
                    <Divider orientation="left" orientationMargin={0}>
                      Summary
                    </Divider>
                    <Typography.Paragraph>
                      {detail?.debrief?.summary}
                    </Typography.Paragraph>
                  </>
                )}

                {detail?.debrief?.decisions?.length > 0 && (
                  <>
                    <Divider orientation="left" orientationMargin={0}>
                      Decisions
                    </Divider>
                    <List
                      className="recording-bullet-list"
                      size="small"
                      dataSource={detail?.debrief?.decisions}
                      renderItem={(item) => <List.Item>{item}</List.Item>}
                    />
                  </>
                )}

                {detail?.debrief?.ghostedTopics?.length > 0 && (
                  <>
                    <Divider orientation="left" orientationMargin={0}>
                      Ghosted Topics
                    </Divider>
                    <List
                      className="recording-bullet-list"
                      size="small"
                      dataSource={detail?.debrief?.ghostedTopics}
                      renderItem={(item) => (
                        <List.Item>
                          <Typography.Text strong>
                            {item?.topic}
                          </Typography.Text>
                          {item?.note ? ` — ${item?.note}` : ''}
                        </List.Item>
                      )}
                    />
                  </>
                )}

                {detail?.debrief?.momMarkdown && (
                  <>
                    <Divider orientation="left" orientationMargin={0}>
                      Minutes of Meeting
                    </Divider>
                    <div className="recording-mom-markdown">
                      <ReactMarkdown>
                        {detail?.debrief?.momMarkdown}
                      </ReactMarkdown>
                    </div>
                  </>
                )}

                {detail?.actionItems?.length > 0 && (
                  <>
                    <Divider orientation="left" orientationMargin={0}>
                      Action Items
                    </Divider>
                    <CommonTable
                      className="recording-action-items-table"
                      size="small"
                      columns={actionItemColumns}
                      dataSource={detail?.actionItems}
                      rowKey={(obj) => obj?.id}
                      bordered
                    />
                  </>
                )}

                {!detail?.debrief && !detail?.audioUrl && (
                  <Empty description="No debrief content is available for this recording." />
                )}
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default ViewRecording;
